/**
 * Sales and payments that were sent but not confirmed yet (ADR 0006). Each one is written to local
 * storage with its idempotency key *before* it is sent and removed once the server answered, so
 * after a crash, a reload or a lost connection the till repeats exactly the same request: the
 * server then returns the original result and never records a sale twice.
 */
import { readJson, writeJson } from "../lib/storage";
import { ApiError, newIdempotencyKey, type ApiClient } from "./client";

/** The till's list; the Tafo app keeps its own (it is another device in the same browser). */
let storageKey = "tafo.pending";

/** Where this page keeps its unconfirmed requests: the Tafo app at /app uses its own list. */
export function choosePendingStore(key: string): void {
  storageKey = key;
}

export interface PendingRequest {
  key: string;
  /** POST for new sales and payments, PUT for edits. Older entries have none: POST. */
  method?: "POST" | "PUT";
  path: string;
  body: unknown;
  /** Shown to the cashier, e.g. "Verkoop € 18,50". */
  label: string;
  /**
   * The authorization of whoever entered a code for this sale (something free, a discount, an edit
   * of a paid order). Valid for 30 minutes; a sale replayed later is refused and reported.
   */
  staffToken?: string;
  createdAt: string;
}

export interface PrepareOptions {
  method?: "POST" | "PUT";
  staffToken?: string | undefined;
}

export function listPending(): PendingRequest[] {
  return readJson<PendingRequest[]>(storageKey, []);
}

/**
 * The requests that are on their way to the server at this moment. They are in the list, so a crash
 * right now does not lose them, but nothing is wrong with them: the screen that sent one shows that.
 */
const sending = new Set<string>();

/**
 * What the cashier is told about ("1 verkoop is nog niet bevestigd door de server") and what is sent
 * again: the requests the server did not answer, never one that is being sent right now. Without this
 * every sale that happened to be on its way when the till looked was reported for a few seconds.
 */
export function listUnconfirmed(): PendingRequest[] {
  return listPending().filter((pending) => !sending.has(pending.key));
}

function save(list: PendingRequest[]): void {
  writeJson(storageKey, list);
}

function remember(request: PendingRequest): void {
  save([...listPending().filter((pending) => pending.key !== request.key), request]);
}

export function forget(key: string): void {
  save(listPending().filter((pending) => pending.key !== key));
}

/**
 * Registers a POST that must happen exactly once, with a new idempotency key, before it is sent.
 * Send it with `resend`; after a network failure call `resend` again with the same request.
 */
export function prepare(path: string, body: unknown, label: string, options: PrepareOptions = {}): PendingRequest {
  const request: PendingRequest = {
    key: newIdempotencyKey(),
    method: options.method ?? "POST",
    path,
    body,
    label,
    ...(options.staffToken !== undefined && { staffToken: options.staffToken }),
    createdAt: new Date().toISOString(),
  };
  remember(request);
  return request;
}

/**
 * Sends a prepared request. Network problems are retried with the same key; when the server stays
 * unreachable the request stays pending and the error is rethrown, so the screen can offer "try
 * again". A refusal by the server (4xx) is final: the request is forgotten.
 */
export async function resend<T>(api: ApiClient, request: PendingRequest): Promise<T> {
  sending.add(request.key);
  try {
    const result = await api.request<T>(request.method ?? "POST", request.path, {
      staffToken: request.staffToken,
      body: request.body,
      idempotencyKey: request.key,
      retry: true,
    });
    forget(request.key);
    return result;
  } catch (error) {
    if (!(error instanceof ApiError && error.retryable)) forget(request.key);
    throw error;
  } finally {
    sending.delete(request.key);
  }
}

/**
 * Sends every unconfirmed request again, oldest first. Returns how many are still unconfirmed
 * (the server is still unreachable for those). A request that is being sent is left to its sender.
 */
export async function replayAll(
  api: ApiClient,
  {
    onSaved,
    onRefused,
  }: { onSaved: (label: string, result: { code: string }) => void; onRefused: (error: unknown) => void },
): Promise<number> {
  for (const request of listUnconfirmed()) {
    // answered or sent again meanwhile, by the screen it belongs to
    if (!listUnconfirmed().some((pending) => pending.key === request.key)) continue;
    try {
      onSaved(request.label, await resend<{ code: string }>(api, request));
    } catch (error) {
      if (!(error instanceof ApiError && error.retryable)) onRefused(error);
    }
  }
  return listUnconfirmed().length;
}
